import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { TranslationPipe } from 'src/tidy-ui-components/pipes/translation.pipe';

import {
  PreferredBrand,
  WALMART_BUSINESS_PREFIX,
  isPreferredBrandBlank,
  isPreferredBrandIncomplete,
  isWalmartProductLink,
  normalizeWalmartLink
} from 'src/models/restocking-brand.model';

/**
 * Editor for an item's "brand waterfall": an ordered, drag-to-reorder list of
 * preferred brands/models used when restocking. Position 1 is the first choice;
 * lower tiers are backups used only when the tier above is unavailable. Each tier
 * captures a brand, a pack size (units per pack) and a Walmart Business product
 * link — mirroring the concierge product-defaults waterfall.
 *
 * The parent seeds `brands` with default brands for the item's category (which
 * the user can then edit), and owns persistence — this component emits the
 * normalized list (positions renumbered 1..n) via `brandsChange` on every edit.
 */
@Component({
  selector: 'tidy-restocking-brand-waterfall',
  templateUrl: './restocking-brand-waterfall.component.html',
  styleUrls: ['./restocking-brand-waterfall.component.scss'],
  standalone: true,
  // TidyUiComponentsModule imports TranslationPipe without re-exporting it, so the
  // `| translate` calls in the tier template need it listed here — without it every
  // tier's embedded view throws on creation and the waterfall renders empty.
  imports: [CommonModule, FormsModule, TidyUiComponentsModule, TranslationPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RestockingBrandWaterfallComponent implements OnChanges {

  @Input() brands: PreferredBrand[] = [];
  @Input() submitted = false;
  @Input() disabled = false;

  @Output() brandsChange = new EventEmitter<PreferredBrand[]>();

  readonly walmartPrefix = WALMART_BUSINESS_PREFIX;

  items: PreferredBrand[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    // Rebuild only when the incoming value differs from what we already hold.
    // The parent echoes our own emit back into [brands]; rebuilding then would
    // replace the tier objects, re-render the list, and steal input focus.
    if (changes.brands && !this.matchesItems(this.brands)) {
      this.items = (this.brands || []).map((brand) => ({ ...brand }));
      this.renumber();
    }
  }

  // A tier the user has started filling in but hasn't completed — the only tiers
  // that surface inline errors. Fully-blank tiers are dropped on save.
  showTierErrors(brand: PreferredBrand): boolean {
    return this.submitted && !isPreferredBrandBlank(brand) && isPreferredBrandIncomplete(brand);
  }

  brandNameInvalid(brand: PreferredBrand): boolean {
    return !brand?.brand?.trim();
  }

  amountInvalid(brand: PreferredBrand): boolean {
    return !(Number(brand?.amount) > 0);
  }

  linkMissing(brand: PreferredBrand): boolean {
    return !brand?.link?.trim();
  }

  // A link was entered but doesn't point at Walmart Business — flagged live, even
  // before a save is attempted.
  linkFormatInvalid(brand: PreferredBrand): boolean {
    return !!brand?.link?.trim() && !isWalmartProductLink(brand.link);
  }

  addBrand(): void {
    this.items.push({ position: this.items.length + 1, brand: '' });
    this.emit();
  }

  removeBrand(index: number): void {
    this.items.splice(index, 1);
    this.renumber();
    this.emit();
  }

  onReorder(reordered: PreferredBrand[]): void {
    this.items = reordered;
    this.renumber();
    this.emit();
  }

  onAmountChange(brand: PreferredBrand, value: unknown): void {
    const parsed = parseInt(String(value ?? ''), 10);
    brand.amount = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    this.emit();
  }

  // Forgive a scheme-less paste so a bare "business.walmart.com/..." still validates.
  onLinkBlur(brand: PreferredBrand): void {
    const normalized = normalizeWalmartLink(brand.link);
    if (normalized !== (brand.link || '')) {
      brand.link = normalized;
      this.emit();
    }
  }

  onFieldChange(): void {
    this.emit();
  }

  // True when `brands` already matches the tiers we hold (i.e. it's the echo of
  // our own emit), so we can skip rebuilding and keep the current DOM/focus.
  private matchesItems(brands: PreferredBrand[]): boolean {
    const list = brands || [];
    if (list.length !== this.items.length) {
      return false;
    }
    return list.every((brand, index) =>
      (brand.brand || '') === (this.items[index].brand || '') &&
      (brand.product_name || '') === (this.items[index].product_name || '') &&
      (brand.link || '') === (this.items[index].link || '') &&
      (brand.amount ?? null) === (this.items[index].amount ?? null) &&
      (brand.notes || '') === (this.items[index].notes || '')
    );
  }

  private renumber(): void {
    this.items.forEach((item, index) => (item.position = index + 1));
  }

  private emit(): void {
    this.brandsChange.emit(this.items.map((item) => ({ ...item })));
  }
}
